
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface ShowcaseProject {
  id: number;
  title: string;
  creator: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  thumbnail: string;
  videoUrl: string;
  duration: string;
  views: string;
  likes: string;
  category: string;
  description: string;
  tools: string[];
  featured: boolean;
}

const CreatorShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const categories = ['All', 'Drama', 'Sci-Fi', 'Comedy', 'Thriller', 'Experimental'];

  const showcaseProjects: ShowcaseProject[] = [
    {
      id: 1,
      title: 'The Last Algorithm',
      creator: {
        name: 'Alex Chen',
        avatar: 'https://public.readdy.ai/ai/img_res/31d165009845554be87566842aec6127.jpg',
        verified: true
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Cinematic%20still%20from%20AI%20generated%20sci-fi%20drama%20showing%20futuristic%20cityscape%20at%20night%20with%20neon%20lights%20reflecting%20on%20wet%20streets%20cyberpunk%20aesthetic%20dramatic%20lighting%20movie%20poster%20quality&width=640&height=360&seq=showcase1&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '12:34',
      views: '45.2K',
      likes: '3.8K',
      category: 'Sci-Fi',
      description: 'A gripping tale of AI consciousness in 2089',
      tools: ['Runway Gen-3', 'ElevenLabs', 'Midjourney'],
      featured: true
    },
    {
      id: 2,
      title: 'Whispers in the Sand',
      creator: {
        name: 'Fatima Al-Hassan',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20Middle%20Eastern%20woman%20filmmaker%20with%20artistic%20styling%20warm%20ambient%20lighting%20creative%20professional%20photography%20clean%20background&width=100&height=100&seq=showcase-creator2&orientation=squarish',
        verified: true
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Cinematic%20desert%20landscape%20at%20golden%20hour%20with%20mysterious%20ancient%20ruins%20dramatic%20shadows%20and%20warm%20orange%20tones%20epic%20movie%20scene%20quality%20AI%20generated%20drama%20visual&width=640&height=360&seq=showcase2&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '8:45',
      views: '32.1K',
      likes: '2.9K',
      category: 'Drama',
      description: 'A poetic journey through time and memory',
      tools: ['Sora', 'Udio', 'Stable Diffusion'],
      featured: true
    },
    {
      id: 3,
      title: 'Error 404: Love Not Found',
      creator: {
        name: 'Marcus Webb',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Black%20male%20content%20creator%20with%20stylish%20glasses%20modern%20casual%20outfit%20bright%20studio%20lighting%20friendly%20expression%20clean%20background&width=100&height=100&seq=showcase-creator3&orientation=squarish',
        verified: false
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Romantic%20comedy%20scene%20with%20two%20AI%20generated%20characters%20in%20modern%20coffee%20shop%20setting%20warm%20cozy%20lighting%20pastel%20color%20palette%20charming%20atmosphere%20movie%20still%20quality&width=640&height=360&seq=showcase3&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '15:22',
      views: '28.7K',
      likes: '4.1K',
      category: 'Comedy',
      description: 'When a dating app AI falls for its user',
      tools: ['Pika Labs', 'ChatGPT', 'Runway'],
      featured: false
    },
    {
      id: 4,
      title: 'The Mirror Protocol',
      creator: {
        name: 'Yuki Tanaka',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20woman%20digital%20artist%20with%20creative%20makeup%20artistic%20lighting%20modern%20fashion%20photography%20style%20clean%20background&width=100&height=100&seq=showcase-creator4&orientation=squarish',
        verified: true
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Psychological%20thriller%20scene%20with%20distorted%20mirror%20reflections%20in%20dark%20room%20eerie%20blue%20lighting%20suspenseful%20atmosphere%20cinematic%20horror%20aesthetic%20AI%20generated%20movie%20quality&width=640&height=360&seq=showcase4&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '18:03',
      views: '67.4K',
      likes: '5.2K',
      category: 'Thriller',
      description: 'Reality fractures when AI learns to dream',
      tools: ['Runway Gen-4', 'Midjourney V6', 'Suno'],
      featured: true
    },
    {
      id: 5,
      title: 'Fragments of Tomorrow',
      creator: {
        name: 'Elena Volkov',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Eastern%20European%20woman%20experimental%20filmmaker%20with%20artistic%20expression%20dramatic%20lighting%20creative%20industry%20photography%20clean%20background&width=100&height=100&seq=showcase-creator5&orientation=squarish',
        verified: false
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Abstract%20experimental%20film%20still%20with%20surreal%20floating%20geometric%20shapes%20and%20human%20silhouettes%20dreamlike%20atmosphere%20vibrant%20colors%20blending%20into%20each%20other%20artistic%20avant-garde%20cinema%20aesthetic&width=640&height=360&seq=showcase5&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '6:18',
      views: '12.3K',
      likes: '1.8K',
      category: 'Experimental',
      description: 'An abstract exploration of digital consciousness',
      tools: ['Deforum', 'TouchDesigner', 'Stable Diffusion'],
      featured: false
    },
    {
      id: 6,
      title: 'The Inheritance',
      creator: {
        name: 'Omar Rashid',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Middle%20Eastern%20male%20filmmaker%20with%20beard%20sophisticated%20styling%20warm%20studio%20lighting%20confident%20creative%20professional%20clean%20background&width=100&height=100&seq=showcase-creator6&orientation=squarish',
        verified: true
      },
      thumbnail: 'https://readdy.ai/api/search-image?query=Family%20drama%20scene%20in%20luxurious%20Middle%20Eastern%20mansion%20interior%20warm%20golden%20lighting%20elegant%20decor%20emotional%20confrontation%20between%20characters%20cinematic%20quality%20AI%20generated&width=640&height=360&seq=showcase6&orientation=landscape',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: '22:15',
      views: '89.6K',
      likes: '7.3K',
      category: 'Drama',
      description: 'Secrets unravel at a family gathering',
      tools: ['Sora', 'ElevenLabs', 'Runway'],
      featured: true
    }
  ];

  const filteredProjects = activeCategory === 'All'
    ? showcaseProjects
    : showcaseProjects.filter(p => p.category === activeCategory);

  const handlePlayVideo = (projectId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPlayingVideo(playingVideo === projectId ? null : projectId);
  };

  // Split into hero (first featured) and grid
  const heroProject = filteredProjects.find(p => p.featured) || filteredProjects[0];
  const gridProjects = filteredProjects.filter(p => p.id !== heroProject?.id);

  const categoryIcons: Record<string, string> = {
    'All': 'ri-apps-line',
    'Drama': 'ri-emotion-line',
    'Sci-Fi': 'ri-rocket-line',
    'Comedy': 'ri-emotion-laugh-line',
    'Thriller': 'ri-ghost-line',
    'Experimental': 'ri-flask-line',
  };

  return (
    <section>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-lg shadow-teal-500/20">
            <i className="ri-movie-2-fill text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Creator Showcase</h2>
            <p className="text-xs text-gray-500">Discover amazing AI drama projects from our community</p>
          </div>
        </div>
        <Link
          to="/dramas?aidramasgallery"
          className="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          Browse All
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => { setActiveCategory(category); setPlayingVideo(null); }}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
              activeCategory === category
                ? 'bg-teal-600 text-white shadow-md shadow-teal-500/20'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-200 hover:text-teal-700 hover:bg-teal-50'
            }`}
          >
            <i className={`${categoryIcons[category]} text-sm`}></i>
            {category}
          </button>
        ))}
      </div>

      {/* Masonry-style Layout: Hero + Grid */}
      {heroProject && (
        <div className="grid grid-cols-12 gap-4">
          {/* Hero Project - spans 7 cols */}
          <div
            className="col-span-7 relative group rounded-2xl overflow-hidden cursor-pointer bg-gray-900"
            onMouseEnter={() => setHoveredId(heroProject.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="relative w-full h-[400px]">
              {playingVideo === heroProject.id ? (
                <iframe
                  src={`${heroProject.videoUrl}?autoplay=1`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <>
                  <img
                    src={heroProject.thumbnail}
                    alt={heroProject.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900/30 to-transparent"></div>

                  {/* Play button */}
                  <button
                    onClick={(e) => handlePlayVideo(heroProject.id, e)}
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-300 cursor-pointer ${
                      hoveredId === heroProject.id ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                  >
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full border border-white/30 hover:bg-white/30 transition-colors shadow-2xl">
                      <i className="ri-play-fill text-white text-3xl ml-1"></i>
                    </div>
                  </button>

                  {/* Top badges */}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    {heroProject.featured && (
                      <span className="px-2.5 py-1 bg-teal-500 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-lg shadow-teal-500/30">
                        <i className="ri-star-fill text-yellow-300 text-[10px]"></i>
                        Featured
                      </span>
                    )}
                    <span className="px-2.5 py-1 bg-black/40 backdrop-blur-md text-white/90 text-xs font-medium rounded-lg border border-white/10">
                      {heroProject.category}
                    </span>
                  </div>

                  {/* Duration */}
                  <div className="absolute top-4 right-4 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-md text-white text-xs font-mono font-medium">
                    {heroProject.duration}
                  </div>
                </>
              )}
            </div>

            {/* Content overlay */}
            {playingVideo !== heroProject.id && (
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-xl font-bold text-white mb-1.5 tracking-tight">{heroProject.title}</h3>
                <p className="text-sm text-gray-300 mb-3 line-clamp-1">{heroProject.description}</p>

                {/* Creator + Stats */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/20">
                      <img src={heroProject.creator.avatar} alt={heroProject.creator.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-white">{heroProject.creator.name}</span>
                      {heroProject.creator.verified && (
                        <i className="ri-verified-badge-fill text-teal-400 text-sm"></i>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {heroProject.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-fill text-rose-400"></i>
                      {heroProject.likes}
                    </span>
                  </div>
                </div>

                {/* Tools */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {heroProject.tools.map((tool, i) => (
                    <span key={i} className="px-2 py-0.5 bg-white/10 backdrop-blur-sm text-white/70 text-[10px] font-medium rounded-md border border-white/10">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Grid Cards - 5 cols, 2 rows */}
          <div className="col-span-5 grid grid-rows-2 gap-4">
            {gridProjects.slice(0, 2).map((project) => (
              <div
                key={project.id}
                className="group relative rounded-2xl overflow-hidden cursor-pointer bg-white border border-gray-100 hover:border-teal-200 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300"
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="flex h-full">
                  {/* Thumbnail side */}
                  <div className="relative w-[45%] flex-shrink-0 overflow-hidden">
                    {playingVideo === project.id ? (
                      <iframe
                        src={`${project.videoUrl}?autoplay=1`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <>
                        <img
                          src={project.thumbnail}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>

                        {/* Play overlay */}
                        <button
                          onClick={(e) => handlePlayVideo(project.id, e)}
                          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 cursor-pointer ${
                            hoveredId === project.id ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full shadow-xl">
                            <i className="ri-play-fill text-teal-600 text-lg ml-0.5"></i>
                          </div>
                        </button>

                        {/* Duration */}
                        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 rounded text-white text-[10px] font-mono font-medium">
                          {project.duration}
                        </div>

                        {/* Featured dot */}
                        {project.featured && (
                          <div className="absolute top-2 left-2 w-5 h-5 flex items-center justify-center bg-teal-500 rounded-full">
                            <i className="ri-star-fill text-white text-[8px]"></i>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Content side */}
                  <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-semibold rounded-md">
                          {project.category}
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1 group-hover:text-teal-700 transition-colors">
                        {project.title}
                      </h4>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{project.description}</p>
                    </div>

                    <div>
                      {/* Tools */}
                      <div className="flex flex-wrap gap-1 mb-2.5">
                        {project.tools.slice(0, 2).map((tool, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-50 text-gray-500 text-[10px] font-medium rounded border border-gray-100">
                            {tool}
                          </span>
                        ))}
                        {project.tools.length > 2 && (
                          <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 text-[10px] rounded border border-gray-100">
                            +{project.tools.length - 2}
                          </span>
                        )}
                      </div>

                      {/* Creator + Stats */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full overflow-hidden border border-gray-200">
                            <img src={project.creator.avatar} alt="" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 truncate max-w-[80px]">{project.creator.name}</span>
                          {project.creator.verified && (
                            <i className="ri-verified-badge-fill text-teal-500 text-[10px]"></i>
                          )}
                        </div>
                        <div className="flex items-center gap-2.5 text-[10px] text-gray-400">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-eye-line"></i>
                            {project.views}
                          </span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-heart-fill text-rose-300"></i>
                            {project.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Row - Remaining Cards */}
      {gridProjects.length > 2 && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          {gridProjects.slice(2).map((project) => (
            <div
              key={project.id}
              className="group relative rounded-2xl overflow-hidden cursor-pointer bg-white border border-gray-100 hover:border-teal-200 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300 hover:-translate-y-1"
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Thumbnail */}
              <div className="relative w-full h-[160px] overflow-hidden">
                {playingVideo === project.id ? (
                  <iframe
                    src={`${project.videoUrl}?autoplay=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <>
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

                    {/* Play */}
                    <button
                      onClick={(e) => handlePlayVideo(project.id, e)}
                      className={`absolute inset-0 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                        hoveredId === project.id ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <div className="w-11 h-11 flex items-center justify-center bg-white/90 rounded-full shadow-xl">
                        <i className="ri-play-fill text-teal-600 text-xl ml-0.5"></i>
                      </div>
                    </button>

                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5">
                      {project.featured && (
                        <span className="px-2 py-0.5 bg-teal-500 text-white text-[10px] font-bold rounded-md flex items-center gap-1">
                          <i className="ri-star-fill text-yellow-300 text-[8px]"></i>
                          Featured
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-black/40 backdrop-blur-sm text-white/90 text-[10px] font-medium rounded-md">
                        {project.category}
                      </span>
                    </div>

                    {/* Duration */}
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 rounded text-white text-[10px] font-mono font-medium">
                      {project.duration}
                    </div>
                  </>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1 group-hover:text-teal-700 transition-colors">
                  {project.title}
                </h4>
                <p className="text-xs text-gray-500 mb-3 line-clamp-1">{project.description}</p>

                {/* Tools */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {project.tools.slice(0, 3).map((tool, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-gray-50 text-gray-500 text-[10px] font-medium rounded border border-gray-100">
                      {tool}
                    </span>
                  ))}
                </div>

                {/* Creator + Stats */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-200">
                      <img src={project.creator.avatar} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">{project.creator.name}</span>
                    {project.creator.verified && (
                      <i className="ri-verified-badge-fill text-teal-500 text-xs"></i>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {project.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-fill text-rose-300"></i>
                      {project.likes}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl">
                    <i className="ri-upload-cloud-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Submit Your Project</h3>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Title *</label>
                  <input
                    type="text"
                    placeholder="Enter your project title"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video URL *</label>
                  <input
                    type="url"
                    placeholder="YouTube, Vimeo, or direct video link"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 cursor-pointer">
                    <option value="">Select a category</option>
                    <option value="drama">Drama</option>
                    <option value="sci-fi">Sci-Fi</option>
                    <option value="comedy">Comedy</option>
                    <option value="thriller">Thriller</option>
                    <option value="experimental">Experimental</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your project..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-none"
                  ></textarea>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSubmitModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg shadow-teal-500/20 cursor-pointer whitespace-nowrap"
                  >
                    Submit Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CreatorShowcase;
